import { useEffect, useState, useSyncExternalStore } from 'react';

import { fetchDocumentEvents, type LinkedEvent } from './document-events-api';

export type { LinkedEvent } from './document-events-api';

export interface DocumentEvents {
  events: readonly LinkedEvent[];
  loading: boolean;
}

const NONE: readonly LinkedEvent[] = [];

// No cache: most links are written on the server (a recording, the agent,
// another person), so a cached list would go stale. The version only lets a
// link made in this client refresh a face that is already open.
const versions = new Map<string, number>();
const listeners = new Set<() => void>();

function versionOf(entityId: string): number {
  return versions.get(entityId) ?? 0;
}

/** Read a document's meetings again wherever they are showing. */
export function invalidateDocumentEvents(entityId: string): void {
  versions.set(entityId, versionOf(entityId) + 1);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The meetings linked to a document, read afresh every time `enabled` turns
 *  true, and `loading` from that render until they arrive. A failed read lists
 *  none: the meeting row is a convenience, never a reason to block the face. */
export function useDocumentEvents(entityId: string, enabled: boolean): DocumentEvents {
  const version = useSyncExternalStore(subscribe, () => versionOf(entityId), () => 0);
  const [read, setRead] = useState<{
    entityId: string;
    open: number;
    events: readonly LinkedEvent[];
  } | null>(null);
  const [opens, setOpens] = useState(0);
  const [wasEnabled, setWasEnabled] = useState(false);
  if (enabled !== wasEnabled) {
    setWasEnabled(enabled);
    if (enabled) setOpens((n) => n + 1);
  }
  const open = opens;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetchDocumentEvents(entityId)
      .then((events) => {
        if (!cancelled) setRead({ entityId, open, events });
      })
      .catch(() => {
        if (!cancelled) setRead({ entityId, open, events: NONE });
      });
    return () => {
      cancelled = true;
    };
  }, [entityId, enabled, version, open]);

  if (!enabled) return { events: NONE, loading: false };
  // Another document's meetings are never shown, not even while loading.
  if (read?.entityId !== entityId) return { events: NONE, loading: true };
  if (read.open !== open) return { events: read.events, loading: true };
  return { events: read.events, loading: false };
}
